import { Component, OnInit, AfterViewInit, ChangeDetectorRef, forwardRef } from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { DomSanitizer } from '@angular/platform-browser';
import { SectionField } from 'app/common/metadata-models/sectionField';
import { DropdownOption } from 'app/common/metadata-models/dropdownOption';
import { DropdownOptionDynamic } from 'app/common/metadata-models/dropdownOptionDynamic';
import { ControlOptions } from 'app/common/metadata-models/controlOptions';
import { FieldWatcherService } from 'app/common/services/field-watcher.service';
import { MetadataApiService } from 'app/common/services/metadata-api.service';
import { BaseControl } from 'app/common/components/controls/base-control';
import { MiscUtil } from 'app/common/utility/miscUtil';
import { environment } from 'environments/environment';
import { LocalizationManagerService } from 'app/common/services/localization-manager.service';
import { AppSharedStateService } from 'app/common/services/app-shared-state.service';
import { AppSharedUtilityService } from 'app/common/services/app-shared-utility.service';
import { InstrumentationService } from 'app/common/services/instrumentation.service';
import { NgbDropdown } from '@ng-bootstrap/ng-bootstrap';
import { Options } from '@popperjs/core';

enum NothingSelectedTextOpt { Metadata, Loading, LoadFailed }

@Component({
    standalone: false,
    selector: 'app-dropdown',
    templateUrl: './dropdown.component.html',
    styleUrls: ['./dropdown.component.css'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => DropdownComponent),
            multi: true
        }
    ]
})
export class DropdownComponent extends BaseControl implements OnInit, AfterViewInit {
    public dropdownData: DropdownOption[] = null;
    public dynamicDrivenByField: SectionField;
    public dropdownOptionsDynamic: DropdownOptionDynamic;
    public nothingSelectedText: DropdownOption = new DropdownOption({text: '', value: null});
    public isDropdownDisabled: boolean = false;
    public isFormDisabled: boolean = false;
    public dropdownPopperOptions = (options: Partial<Options>): Partial<Options> => ({
        ...options,
        modifiers: [
            ...(options.modifiers || []).filter((modifier) => modifier.name !== 'flip'),
            {name: 'flip', enabled: false}
        ]
    });
    private prevSelectedValue: number|string|boolean = null;
    private initialValueProcessingComplete: boolean = false;
    private typeaheadText: string = '';
    private typeaheadTimer: ReturnType<typeof setTimeout>;

    // Constructor for DropdownComponent.
    constructor(
        protected fieldWatcherService: FieldWatcherService,
        protected metadataApiService: MetadataApiService,
        protected changeDetectorRef: ChangeDetectorRef,
        protected domSanitizer: DomSanitizer,
        protected appSharedStateService: AppSharedStateService,
        protected appSharedUtilityService: AppSharedUtilityService,
        private instrumentationService: InstrumentationService,
        public lms: LocalizationManagerService /* Using short name on purpose for data binding. */) {
        super(fieldWatcherService, metadataApiService, changeDetectorRef, domSanitizer, appSharedStateService, appSharedUtilityService, lms);
    }

    // Angular OnInit handler. Part of the lifecycle hooks: https://angular.io/guide/lifecycle-hooks
    public ngOnInit(): void {
    }

    // Angular AfterViewInit handler. Part of the lifecycle hooks: https://angular.io/guide/lifecycle-hooks
    public ngAfterViewInit(): void {
        // This is done in a setTimeout so it defers running until a data binding pass is done first.
        setTimeout(() => {
            this.setNothingSelectedText(NothingSelectedTextOpt.Metadata);

            const controlOptions: ControlOptions = this.fieldData.metadata.controlOptions;
            if (!!controlOptions.dropdownOptionsStatic) {
                this.dropdownData = this.fieldData.metadata.controlOptions.dropdownOptionsStatic;

                // If dropdownData is only a list of one item - then pre-select that one item automatically.
                if (this.dropdownData.length === 1) {
                    this.setDropdownValue(this.dropdownData[0].value);
                }
            } else if (!!controlOptions.dropdownOptionsDynamic) {
                // Disable this control until the watched field has a change. It is empty until a change occurs,
                // so do not have an enabled dropdown with nothing in it yet. Will be enabled once it gets populated
                // after watched field changes.
                this.isDropdownDisabled = true;
                this.dropdownOptionsDynamic = this.fieldData.metadata.controlOptions.dropdownOptionsDynamic;

                if (!!this.dropdownOptionsDynamic) {
                    // Set the drivenByFieldLabel based on the dropdownOptionsDynamic.drivenByFieldId.
                    this.dynamicDrivenByField = this.pageMetadataState.findFieldRecursive(this.dropdownOptionsDynamic.drivenByFieldId);
                    // If the drivenByFieldId is in the parent chain above this control (that is, this control is in the childFields of the parent
                    // or grandparents). We should not set up a field watcher for that drivenByFieldId as when it changes then a whole new set
                    // of children would be retrieved, wiping out this control instance. So, in this case, just immediately call getDynamicDropdownData
                    // to get the dynamic dropdown values based on the already selected value in the dynamicDrivenByField.
                    if (this.pageMetadataState.isFieldDescendantOf(this.fieldData.id, this.dropdownOptionsDynamic.drivenByFieldId)) {
                        this.setupDynamicDropdown(false);
                    } else {
                        this.setupDynamicDropdown();
                    }
                }
            }

            // Adding field watchers must be done in ngAfterViewInit.
            this.addCommonFieldWatchers();

            // Mark this control as ready after the field watchers have been set.
            this.fieldData.isControlReady = true;

            // Manually trigger a change for the control if there was a value initially set.
            if (!MiscUtil.isNullOrUndefinedOrEmptyString(this.fieldData.fieldValue)) {
                // Run this once all controls are ready and have registered their own field watchers.
                this.pageMetadataState.runWhenControlsReadyForActiveSection(() => {
                    this.onValueChange(this.fieldData.fieldValue);
                    this.initialValueProcessingComplete = true;
                });
            } else {
                this.initialValueProcessingComplete = true;
            }
        });
    }

    public writeValue(value: number|string|boolean): void {
        const normalizedValue = value === undefined ? null : value;
        super.writeValue(normalizedValue);

        if (this.initialValueProcessingComplete) {
            this.prevSelectedValue = normalizedValue;
        }
    }

    // Set up the dynamic dropdown.
    private setupDynamicDropdown(useFieldWatcher: boolean = true): void {
        // If there is no dropdownOptionsDynamic then do nothing and return.
        if (!this.dropdownOptionsDynamic) {
            return;
        }

        // If the driven by field has no value (null or undefined or empty string) then just set
        // dropdownOptionsDynamic.initialLoadComplete to true.
        if (MiscUtil.isNullOrUndefinedOrEmptyString(this.dynamicDrivenByField.fieldValue)) {
            this.dropdownOptionsDynamic.initialLoadComplete = true;
        } else {
            this.dropdownOptionsDynamic.initialLoadComplete = false;
        }

        const callback = (changedField: SectionField) => {
            console.log(`Dropdown field ${this.fieldData.id} got a change notification from field ${this.dropdownOptionsDynamic.drivenByFieldId} and value is: ${changedField.fieldValue}`);

            // Set the dropdownOptionsDynamic.isLoadingOrReloading flag to true.
            this.dropdownOptionsDynamic.isLoadingOrReloading = true;

            // If section data is not loading then null out the field value for this control. Since the
            // drivenByFieldId (parent control) has changed, then user must re-select something for this control.
            // If section data is loading then we don't want to null out the field value for this control.
            if (!this.pageMetadataState.sectionFieldsLoading) {
                this.isDropdownDisabled = true;
                this.setDropdownValue(null);
            }

            this.markControlAsTouched();

            if (MiscUtil.isNullOrUndefined(changedField.fieldValue)) {
                // Clear out the data for this dropdown.
                this.dropdownData = null;
                this.isDropdownDisabled = true;
                // Set the dropdownOptionsDynamic.initialLoadComplete flag to true as there was no data to load.
                this.dropdownOptionsDynamic.initialLoadComplete = true;
                // Set the dropdownOptionsDynamic.isLoadingOrReloading flag to false.
                this.dropdownOptionsDynamic.isLoadingOrReloading = false;
            } else {
                this.setNothingSelectedText(NothingSelectedTextOpt.Loading);
                setTimeout(() => {
                    // Call the API specified in the dropdownOptionsDynamic.
                    this.metadataApiService.getDynamicDropdownData(this.dropdownOptionsDynamic, this.fieldData.id,
                        this.dropdownOptionsDynamic.drivenByFieldId, changedField.fieldValue, this.pageMetadataState)
                        .subscribe((dropdownOptions: DropdownOption[]) => {
                            this.setNothingSelectedText(NothingSelectedTextOpt.Metadata);
                            this.dropdownData = dropdownOptions;
                            this.isDropdownDisabled = false;
                            // Set the dropdownOptionsDynamic.initialLoadComplete flag to true.
                            this.dropdownOptionsDynamic.initialLoadComplete = true;
                            // Set the dropdownOptionsDynamic.isLoadingOrReloading flag to false.
                            this.dropdownOptionsDynamic.isLoadingOrReloading = false;

                            // If dropdownOptions is only a list of one item - then pre-select that one item automatically.
                            // This is just a basic user assistance thing - where we select the one item automatically.
                            if (dropdownOptions.length === 1) {
                                this.setDropdownValue(dropdownOptions[0].value);
                            }
                        }, (error: any) => {
                            // The call failed. The CustomErrorHandlerService will cause an error to display.
                            this.setNothingSelectedText(NothingSelectedTextOpt.LoadFailed);
                            this.dropdownOptionsDynamic.initialLoadComplete = true;
                            this.dropdownOptionsDynamic.isLoadingOrReloading = false;
                        });
                }, environment.useLocalMockData ? environment.useLocalMockDataSimulatedDelay : 0); // If using local mock data, then simulate a delay.
            }
        };

        // If useFieldWatcher is true then add a field watcher to watch for changes in the drivenByFieldId. The callback will be called whenever
        // that field changes. Otherwise if this is false then just call the callback right now.
        if (useFieldWatcher) {
            this.fieldWatcherService.addFieldWatcher(this.dropdownOptionsDynamic.drivenByFieldId, callback);
        } else {
            callback(this.dynamicDrivenByField);
        }
    }

    // Dropdown value changed handler.
    public onValueChange(value: number|string|boolean): void {
        // If value or this.prevSelectedValue were undefined, make them null. This is to eliminate problems due to undefined !== null.
        if (value === undefined) {
            value = null;
        }
        if (this.prevSelectedValue === undefined) {
            this.prevSelectedValue = null;
        }

        if (this.prevSelectedValue !== value) {
            this.notifyChanges();
            if (!this.pageMetadataState.sectionFieldsLoading && (!this.dropdownOptionsDynamic || !this.dropdownOptionsDynamic.isLoadingOrReloading)) {
                this.instrumentationService.trackEvent(
                    `Dropdown change:  ${this.fieldData.id}`,
                    {
                        id: this.fieldData.id,
                        value: this.getSelectedOptionText(value),
                        correlationId: this.instrumentationService.generateCorrelationId(),
                        referenceId: this.appSharedStateService.referenceId,
                        userKey: this.appSharedStateService.userKey
                    }
                );
            }

            this.prevSelectedValue = value;
        }
    }

    public get hasSelection(): boolean {
        return !MiscUtil.isNullOrUndefined(this.fieldData.fieldValue);
    }

    public get isLoading(): boolean {
        return (!!this.dropdownOptionsDynamic && this.dropdownOptionsDynamic.isLoadingOrReloading) ||
            (!!this.fieldData.childFieldsDynamic && this.fieldData.childFieldsDynamic.isReloading) ||
            (!!this.fieldData.sectionsDynamic && this.fieldData.sectionsDynamic.isReloading);
    }

    public get selectedOptionText(): string {
        return this.hasSelection ? this.getSelectedOptionText(this.fieldData.fieldValue) : this.nothingSelectedText.text;
    }

    public get isControlDisabled(): boolean {
        return this.isFormDisabled || this.isDropdownDisabled || !!this.fieldData.metadata.readOnly;
    }

    public get isControlInvalid(): boolean {
        const formControl = this.form?.controls[this.fieldData.id];
        return !this.isControlDisabled && !!formControl && formControl.invalid && formControl.touched;
    }

    public setDisabledState(isDisabled: boolean): void {
        this.isFormDisabled = isDisabled;
    }

    public selectOption(value: number|string|boolean): void {
        this.setDropdownValue(value, true);
    }

    public onDropdownBlur(): void {
        this.markControlAsTouched();
    }

    public onDropdownOpenChange(isOpen: boolean): void {
        if (isOpen) {
            document.getElementById(this.fieldData.id)?.scrollIntoView({block: 'center'});
        } else {
            this.typeaheadText = '';
            clearTimeout(this.typeaheadTimer);
        }
    }

    public onDropdownKeydown(event: KeyboardEvent, dropdown: NgbDropdown): void {
        if (event.key.length !== 1 || event.ctrlKey || event.altKey || event.metaKey) {
            return;
        }

        clearTimeout(this.typeaheadTimer);
        this.typeaheadText += event.key.toLocaleLowerCase();

        const matchingIndex = this.dropdownData?.findIndex((option: DropdownOption) =>
            option.text.toLocaleLowerCase().startsWith(this.typeaheadText)) ?? -1;

        if (matchingIndex >= 0) {
            dropdown.open();
            setTimeout(() => document.getElementById(`${this.fieldData.id}-option-${matchingIndex + 1}`)?.focus());
        }

        this.typeaheadTimer = setTimeout(() => this.typeaheadText = '', 700);
    }

    private getSelectedOptionText(value: number|string|boolean): string {
        const selectedOption = this.dropdownData?.find((option: DropdownOption) => option.value === value);
        return selectedOption?.text || '';
    }

    private setDropdownValue(value: number|string|boolean, markAsTouched: boolean = false): void {
        const normalizedValue = value === undefined ? null : value;
        const valueChanged = this.fieldData.fieldValue !== normalizedValue;

        this.fieldData.fieldValue = normalizedValue;

        if (valueChanged) {
            this.propagateValueChange(normalizedValue);
            this.onValueChange(normalizedValue);
        }

        if (markAsTouched) {
            this.markControlAsTouched();
        }
    }

    // Fire change notification.
    private notifyChanges(): void {
        // Fire the change notification within a setTimeout. Reason is form control validation is not done until until the UI has
        // had a chance to process the value change. For a dropdown marked as required, when changing from nothing selected to
        // a selected value - then it is initially invalid, but after a setTimeout when the dropdown has a selection then the control
        // is valid.
        setTimeout(() => {
            this.fieldWatcherService.notifyChanges(this.fieldData);
        });
    }

    // Sets nothing selected text (the default item in the dropdown when nothing is selected).
    private setNothingSelectedText(nothingSelectedTextOpt: NothingSelectedTextOpt): void {
        switch (nothingSelectedTextOpt) {
            case NothingSelectedTextOpt.Metadata:
                this.nothingSelectedText = new DropdownOption({text: this.fieldData.metadata.controlOptions.nullSelectionText, value: null});
                break;
            case NothingSelectedTextOpt.Loading:
                this.nothingSelectedText = new DropdownOption({text: this.lms.get('LOADING_ELLIPSIS'), value: null});
                break;
            case NothingSelectedTextOpt.LoadFailed:
                this.nothingSelectedText = new DropdownOption({text: this.lms.get('LOAD_FAILED'), value: null});
                break;
        }
    }
}
